import type { CloudConfig, CurrentUser } from "./types";

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient(options: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; error?: string }) => void;
          }): { requestAccessToken(options?: { prompt?: string }): void };
        };
      };
    };
    AppleID?: {
      auth: {
        init(options: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state: string;
          usePopup: boolean;
        }): void;
        signIn(): Promise<{ authorization?: { id_token?: string }; user?: { email?: string; name?: { firstName?: string; lastName?: string } } }>;
      };
    };
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";
const APPLE_SIGNIN_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const CURRENT_USER_KEY = "allmanual.currentUser";

export function isRealCurrentUser(user: CurrentUser | null): user is CurrentUser {
  if (!user || user.mode !== "live") return false;
  if (user.provider !== "google" && user.provider !== "apple") return false;
  if (user.ownerId !== `${user.provider}:${user.userId}`) return false;
  if (!user.userId || /demo/i.test(user.userId) || /demo/i.test(user.ownerId) || /demo/i.test(user.displayName)) return false;
  return true;
}

function loadScript(src: string, id: string) {
  return new Promise<void>((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function parseJwt(token: string): Record<string, any> {
  const payload = token.split(".")[1];
  if (!payload) return {};
  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  const decoded = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
  return JSON.parse(decoded);
}

export function loadCurrentUser(): CurrentUser | null {
  try {
    const user = JSON.parse(sessionStorage.getItem(CURRENT_USER_KEY) || localStorage.getItem(CURRENT_USER_KEY) || "null");
    if (isRealCurrentUser(user)) return user;
    clearCurrentUser();
    return null;
  } catch {
    clearCurrentUser();
    return null;
  }
}

export function saveCurrentUser(user: CurrentUser) {
  if (!isRealCurrentUser(user)) throw new Error("실제 Google 또는 Apple 로그인 사용자만 저장할 수 있습니다.");
  sessionStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
}

export function clearCurrentUser() {
  sessionStorage.removeItem(CURRENT_USER_KEY);
  localStorage.removeItem(CURRENT_USER_KEY);
  sessionStorage.removeItem("allmanual.google.accessToken");
  sessionStorage.removeItem("allmanual.apple.connected");
}

export async function signInWithGoogle(config?: CloudConfig["google"]): Promise<CurrentUser> {
  if (!config?.enabled || !config.clientId) {
    throw new Error("Google 로그인 설정이 필요합니다: GOOGLE_CLIENT_ID, GOOGLE_API_KEY");
  }

  await loadScript(GIS_SRC, "google-gis");
  const token = await new Promise<string>((resolve, reject) => {
    const client = window.google?.accounts?.oauth2?.initTokenClient({
      client_id: config.clientId,
      scope: "openid profile email",
      callback: (response) => {
        if (response.error || !response.access_token) return reject(new Error(response.error || "Google token missing"));
        sessionStorage.setItem("allmanual.google.accessToken", response.access_token);
        resolve(response.access_token);
      }
    });
    client?.requestAccessToken({ prompt: "consent" });
  });

  const profile = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
    headers: { Authorization: `Bearer ${token}` }
  }).then((response) => {
    if (!response.ok) throw new Error("Google profile lookup failed");
    return response.json();
  });

  const user: CurrentUser = {
    provider: "google",
    userId: profile.sub,
    ownerId: `google:${profile.sub}`,
    displayName: profile.name || profile.email || "Google User",
    email: profile.email,
    mode: "live"
  };
  saveCurrentUser(user);
  return user;
}

export async function signInWithApple(config?: CloudConfig["apple"]): Promise<CurrentUser> {
  if (!config?.enabled || !config.signInClientId || !config.signInRedirectUri) {
    throw new Error("Apple 로그인 설정이 필요합니다: APPLE_SIGNIN_CLIENT_ID, APPLE_SIGNIN_REDIRECT_URI, APPLE_CLOUDKIT_CONTAINER, APPLE_CLOUDKIT_API_TOKEN");
  }

  await loadScript(APPLE_SIGNIN_SRC, "apple-signin-js");
  window.AppleID?.auth.init({
    clientId: config.signInClientId,
    scope: "name email",
    redirectURI: config.signInRedirectUri,
    state: crypto.randomUUID(),
    usePopup: true
  });
  const result = await window.AppleID?.auth.signIn();
  const claims = result?.authorization?.id_token ? parseJwt(result.authorization.id_token) : {};
  const firstName = result?.user?.name?.firstName || "";
  const lastName = result?.user?.name?.lastName || "";
  const displayName = [firstName, lastName].filter(Boolean).join(" ") || result?.user?.email || claims.email || "Apple User";
  const userId = claims.sub || crypto.randomUUID();
  const user: CurrentUser = {
    provider: "apple",
    userId,
    ownerId: `apple:${userId}`,
    displayName,
    email: result?.user?.email || claims.email,
    mode: "live"
  };
  saveCurrentUser(user);
  return user;
}
